import { useState, useEffect, useRef } from 'react';
import { getAccessToken, API_ENDPOINTS } from '../../api';
import { uploadFileToS3 } from '../../utils/s3Upload';
import { compressImage } from '../../utils/imageCompress';
import { OFFER_CATEGORIES } from '../../constants/categories';

const EMPTY_FORM = { id: null, title: '', type: '', store: '', actualPrice: '', currentPrice: '', link: '', image: '', description: '', couponCode: '' };

const STORE_PRESETS = ['Flipkart', 'Amazon', 'Myntra', 'Ajio'];

function deriveStoreChoice(store) {
  if (!store) return '';
  return STORE_PRESETS.includes(store) ? store : '__other__';
}

function OffersSection() {
  const [items, setItems] = useState([]);
  const [loadingOffers, setLoadingOffers] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [filterCategory, setFilterCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [storeChoice, setStoreChoice] = useState('');
  const [bulkDeleteCount, setBulkDeleteCount] = useState('');
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const fileInputRef = useRef();

  useEffect(() => {
    fetchOffers(filterCategory, activeSearch, null, true);
  }, [filterCategory, activeSearch]);

  async function fetchOffers(category, searchQuery, lek, replace = false) {
    replace ? setLoadingOffers(true) : setLoadingMore(true);
    try {
      const params = new URLSearchParams({ limit: 6 });
      if (category !== 'All') params.set('category', category);
      if (searchQuery) params.set('search', searchQuery);
      if (lek) params.set('lastEvaluatedKey', JSON.stringify(lek));
      const response = await fetch(`${API_ENDPOINTS.OFFERS}?${params}`);
      if (response.ok) {
        const data = await response.json();
        const newItems = data.offers || data;
        setItems(prev => replace ? newItems : [...prev, ...newItems]);
        setLastEvaluatedKey(data.lastEvaluatedKey || null);
        setHasMore(!!data.lastEvaluatedKey);
      }
    } catch (err) {
      console.error('Failed to fetch offers:', err);
    } finally {
      replace ? setLoadingOffers(false) : setLoadingMore(false);
    }
  }

  function loadMore() {
    fetchOffers(filterCategory, activeSearch, lastEvaluatedKey);
  }

  function handleSearch(e) {
    e.preventDefault();
    setActiveSearch(search.trim());
    setItems([]);
    setLastEvaluatedKey(null);
    setHasMore(true);
  }

  function clearSearch() {
    setSearch('');
    setActiveSearch('');
    setItems([]);
    setLastEvaluatedKey(null);
    setHasMore(true);
  }

  function handleCategoryFilter(category) {
    setFilterCategory(category);
    setItems([]);
    setLastEvaluatedKey(null);
    setHasMore(true);
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleStoreSelectChange = (e) => {
    const v = e.target.value;
    setStoreChoice(v);
    if (v === '__other__') {
      if (STORE_PRESETS.includes(formData.store)) {
        setFormData({ ...formData, store: '' });
      }
    } else {
      setFormData({ ...formData, store: v });
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const compressed = await compressImage(file, 100);
    setImageFile(compressed);
  };

  const handleAdd = async () => {
    if (!formData.title || !formData.type) {
      alert('Please fill in Title and Type');
      return;
    }
    if (!formData.actualPrice || !formData.currentPrice) {
      alert('Please fill in Actual Price and Current Price');
      return;
    }
    let imageUrl = formData.image;
    if (imageFile) {
      setUploading(true);
      try {
        imageUrl = await uploadFileToS3(imageFile, 'offers');
      } catch (err) {
        alert('Image upload failed: ' + err.message);
        setUploading(false);
        return;
      }
      setUploading(false);
    }
    const { id: _id, ...offerPayload } = { ...formData, image: imageUrl };
    try {
      const response = await fetch(API_ENDPOINTS.OFFERS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAccessToken()}`,
        },
        body: JSON.stringify(offerPayload),
      });
      if (!response.ok) throw new Error(`Failed to add offer (${response.status})`);
      const saved = await response.json();
      setItems([...items, saved]);
      setFormData(EMPTY_FORM);
      setStoreChoice('');
      setImageFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      alert('Offer added successfully!');
    } catch (err) {
      alert('Failed to add offer: ' + err.message);
    }
  };

  const handleEdit = (item) => {
    setFormData(item);
    setStoreChoice(deriveStoreChoice(item.store));
    setIsEditing(true);
  };

  const handleUpdate = async () => {
    let imageUrl = formData.image;
    if (imageFile) {
      setUploading(true);
      try {
        imageUrl = await uploadFileToS3(imageFile, 'offers');
      } catch (err) {
        alert('Image upload failed: ' + err.message);
        setUploading(false);
        return;
      }
      setUploading(false);
    }
    try {
      const response = await fetch(`${API_ENDPOINTS.OFFERS}/${formData.id}?type=${encodeURIComponent(formData.type)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAccessToken()}`,
        },
        body: JSON.stringify({ ...formData, image: imageUrl }),
      });
      if (!response.ok) throw new Error(`Failed to update offer (${response.status})`);
      const updated = await response.json();
      setItems(items.map(item => item.id === formData.id ? updated : item));
      setFormData(EMPTY_FORM);
      setStoreChoice('');
      setImageFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setIsEditing(false);
      alert('Offer updated successfully!');
    } catch (err) {
      alert('Failed to update offer: ' + err.message);
    }
  };

  const handleDelete = async (id, category) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      const response = await fetch(`${API_ENDPOINTS.OFFERS}/${id}?type=${encodeURIComponent(category)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getAccessToken()}` },
      });
      if (!response.ok) throw new Error(`Failed to delete offer (${response.status})`);
      setItems(items.filter(item => item.id !== id));
    } catch (err) {
      alert('Failed to delete offer: ' + err.message);
    }
  };

  const handleBulkDelete = async () => {
    const n = parseInt(bulkDeleteCount, 10);
    if (!n || n <= 0) {
      alert('Enter a valid number of offers to delete');
      return;
    }
    const categoryLabel = filterCategory !== 'All' ? ` in "${filterCategory}"` : '';
    if (!window.confirm(`Delete the ${n} oldest offer(s)${categoryLabel}? This cannot be undone.`)) {
      return;
    }
    setBulkDeleting(true);
    try {
      const params = new URLSearchParams({ count: n });
      if (filterCategory !== 'All') params.set('category', filterCategory);
      const response = await fetch(`${API_ENDPOINTS.OFFERS}/bulk/oldest?${params}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getAccessToken()}` },
      });
      if (!response.ok) throw new Error(`Bulk delete failed (${response.status})`);
      const result = await response.json();
      setItems([]);
      setLastEvaluatedKey(null);
      setHasMore(true);
      fetchOffers(filterCategory, activeSearch, null, true);
      setBulkDeleteCount('');
      alert(result.deletedCount > 0
        ? `Deleted ${result.deletedCount} offer(s):\n${result.deletedTitles.join('\n')}`
        : 'No offers found to delete.');
    } catch (err) {
      alert('Bulk delete failed: ' + err.message);
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleCancel = () => {
    setFormData(EMPTY_FORM);
    setStoreChoice('');
    setImageFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setIsEditing(false);
  };

  const inputStyle = { padding: 12, borderRadius: 8, border: '1px solid #a5b4fc', fontSize: '1rem' };


  return (
    <>
      {/* Offer Form */}
      <div style={{ background: '#fff', padding: 24, borderRadius: 16, boxShadow: '0 2px 16px #e0e7ff', marginBottom: 32 }}>
        <h2 style={{ marginBottom: 16, color: '#6366f1' }}>🏷️ {isEditing ? 'Edit Offer' : 'Add New Offer'}</h2>
        <div className="admin-form-grid">
          <input name="title" placeholder="Title *" value={formData.title} onChange={handleInputChange} style={inputStyle} />
          <select name="type" value={formData.type} onChange={handleInputChange}
            style={{ ...inputStyle, color: formData.type ? '#1e293b' : '#94a3b8' }}>
            <option value="">Select Category *</option>
            {OFFER_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <select
              value={storeChoice}
              onChange={handleStoreSelectChange}
              style={{ ...inputStyle, color: storeChoice ? '#1e293b' : '#94a3b8' }}
            >
              <option value="">Store (optional)</option>
              {STORE_PRESETS.map(s => <option key={s} value={s}>{s}</option>)}
              <option value="__other__">Others (specify name)</option>
            </select>
            {storeChoice === '__other__' && (
              <input
                name="store"
                placeholder="Enter store name"
                value={formData.store || ''}
                onChange={handleInputChange}
                style={inputStyle}
                autoFocus
              />
            )}
          </div>
          <input name="actualPrice" type="number" placeholder="Actual Price / MRP (e.g. 1000)" min="0" value={formData.actualPrice} onChange={handleInputChange} style={inputStyle} />
          <input name="currentPrice" type="number" placeholder="Current Price / Sale Price (e.g. 300)" min="0" value={formData.currentPrice} onChange={handleInputChange} style={inputStyle} />
          <input name="link" placeholder="Affiliate Link (https://...)" value={formData.link} onChange={handleInputChange} style={inputStyle} />
          <input name="couponCode" placeholder="Coupon Code (optional, e.g. SBI10)" value={formData.couponCode || ''} onChange={handleInputChange} style={inputStyle} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" ref={fileInputRef} onChange={handleFileChange}
              style={{ padding: 8, borderRadius: 8, border: '1px solid #a5b4fc', fontSize: '1rem' }} />
            {imageFile && <span style={{ fontSize: '0.85rem', color: '#6366f1' }}>Selected: {imageFile.name}</span>}
            {uploading && <span style={{ color: '#f59e42', fontSize: '0.9rem' }}>Uploading image...</span>}
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Description"
              rows={6}
              style={{ ...inputStyle, width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
            />
          </div>
        </div>
        <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
          {isEditing ? (
            <>
              <button onClick={handleUpdate} style={{ padding: '10px 24px', borderRadius: 8, background: '#6366f1', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}>
                Update Offer
              </button>
              <button onClick={handleCancel} style={{ padding: '10px 24px', borderRadius: 8, background: '#e5e7eb', color: '#374151', border: 'none', fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
            </>
          ) : (
            <button onClick={handleAdd} style={{ padding: '10px 24px', borderRadius: 8, background: '#6366f1', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}>
              + Add Offer
            </button>
          )}
        </div>
      </div>

      {/* Offers List */}
      <div style={{ background: '#fff', padding: 24, borderRadius: 16, boxShadow: '0 2px 16px #e0e7ff', marginBottom: 32 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>Manage Offers ({items.length})</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600 }}>Delete last</label>
            <input
              type="number"
              min="1"
              placeholder="N"
              value={bulkDeleteCount}
              onChange={e => setBulkDeleteCount(e.target.value)}
              style={{ width: 64, padding: '6px 8px', borderRadius: 6, border: '1px solid #a5b4fc', fontSize: '0.85rem' }}
            />
            <button
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              style={{ padding: '6px 14px', borderRadius: 6, background: '#f43f5e', color: '#fff', border: 'none', fontWeight: 600, fontSize: '0.85rem', cursor: bulkDeleting ? 'not-allowed' : 'pointer', opacity: bulkDeleting ? 0.7 : 1 }}
            >
              {bulkDeleting ? 'Deleting...' : 'Delete Last N'}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', fontSize: '1rem', color: '#a5b4fc', pointerEvents: 'none' }}>🔍</span>
            <input
              type="text"
              placeholder="Search offers by title..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', padding: '10px 36px 10px 34px', borderRadius: 10, border: '1.5px solid #c7d2fe', fontSize: '0.95rem', boxSizing: 'border-box', outline: 'none', boxShadow: '0 2px 8px rgba(99,102,241,0.08)', background: '#fff' }}
            />
            {search && (
              <button type="button" onClick={clearSearch}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '1.1rem', lineHeight: 1 }}>
                ✕
              </button>
            )}
          </div>
          <button type="submit"
            style={{ padding: '10px 18px', borderRadius: 10, background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', color: '#fff', border: 'none', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(99,102,241,0.25)', fontSize: '0.9rem' }}>
            Search
          </button>
        </form>
        {activeSearch && (
          <div style={{ marginBottom: 10, color: '#6366f1', fontSize: '0.9rem' }}>
            Showing results for <strong>"{activeSearch}"</strong>
            <button onClick={clearSearch} style={{ marginLeft: 10, background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline' }}>
              Clear
            </button>
          </div>
        )}

        {/* Category Filter */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          {['All', ...OFFER_CATEGORIES].map(cat => (
            <button key={cat} onClick={() => handleCategoryFilter(cat)}
              style={{
                padding: '6px 14px', borderRadius: 20, border: 'none', fontWeight: 600,
                fontSize: '0.82rem', cursor: 'pointer',
                background: filterCategory === cat ? '#6366f1' : '#eef2ff',
                color: filterCategory === cat ? '#fff' : '#4338ca',
              }}>
              {cat}
            </button>
          ))}
        </div>
        {loadingOffers && <p style={{ color: '#6366f1' }}>Loading offers...</p>}
        {!loadingOffers && items.length === 0 && (
          <p style={{ textAlign: 'center', color: '#888', marginTop: 24 }}>No offers yet. Add your first offer above!</p>
        )}
        {!loadingOffers && items.length === 0 && activeSearch && (
          <p style={{ textAlign: 'center', color: '#888', marginTop: 24 }}>No offers found for "<strong>{activeSearch}</strong>"</p>
        )}
        {!loadingOffers && items.length > 0 && (
          <>
            <div style={{ overflowX: 'auto', maxHeight: 480, overflowY: 'auto', borderRadius: 10, border: '1px solid #e0e7ff' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 500 }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr style={{ background: '#eef2ff' }}>
                    <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #a5b4fc' }}>Title</th>
                    <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #a5b4fc' }}>Type</th>
                    <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #a5b4fc' }}>Price</th>
                    <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #a5b4fc' }}>Coupon</th>
                    <th style={{ padding: 12, textAlign: 'center', borderBottom: '2px solid #a5b4fc' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => (
                    <tr key={item.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: 12 }}>{item.title}</td>
                      <td style={{ padding: 12 }}>{item.type}</td>
                      <td style={{ padding: 12 }}>
                        {item.currentPrice && <span style={{ fontWeight: 700, color: '#16a34a' }}>₹{item.currentPrice}</span>}
                        {item.actualPrice && <span style={{ marginLeft: 6, textDecoration: 'line-through', color: '#94a3b8', fontSize: '0.85rem' }}>₹{item.actualPrice}</span>}
                        {item.offerPercentage && <span style={{ marginLeft: 6, color: '#ef4444', fontWeight: 700, fontSize: '0.82rem' }}>{item.offerPercentage}% OFF</span>}
                      </td>
                      <td style={{ padding: 12 }}>
                        {item.couponCode
                          ? <span style={{ fontWeight: 700, color: '#b45309', background: '#fffbeb', border: '1px dashed #f59e0b', borderRadius: 6, padding: '2px 8px', fontSize: '0.82rem' }}>{item.couponCode}</span>
                          : <span style={{ color: '#cbd5e1', fontSize: '0.82rem' }}>—</span>}
                      </td>
                      <td style={{ padding: 12, textAlign: 'center' }}>
                        <button onClick={() => handleEdit(item)} style={{ marginRight: 8, padding: '6px 14px', borderRadius: 6, background: '#fbbf24', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}>
                          Edit
                        </button>
                        <button onClick={() => handleDelete(item.id, item.type)} style={{ padding: '6px 14px', borderRadius: 6, background: '#f43f5e', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' }}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hasMore && (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  style={{ padding: '10px 28px', borderRadius: 8, background: '#eef2ff', color: '#6366f1', border: '2px solid #6366f1', fontWeight: 700, fontSize: '0.9rem', cursor: loadingMore ? 'not-allowed' : 'pointer', opacity: loadingMore ? 0.7 : 1 }}
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default OffersSection;
